import { useEffect, useMemo } from 'react';

import { useMapPack } from './pack-loader.js';
import { adopt, release } from '../../../vendor/maps/scenario.js';
import { packGeometry } from '../model/map-pack.js';
import { scenarioModel, type ScenarioModel } from '../model/scenario.js';
import type { MwGeometry } from '../model/mw-map-records.js';
import type { CellViewWire } from '@badger-sentinel/protocol';

/**
 * The scenario facade, for every map on the console.
 *
 * One call site, in `ConsoleShell`, because adoption cannot belong to the
 * theatre pane's lifetime: `mw-map.js` **extends** `region-map`, so the warning
 * board boots through the same `import('./scenario.js')` and reads the same
 * `REGIONS` to draw the ground under its launch tracks. A model adopted when the
 * theatre tab mounted and released when it unmounted would take the geography
 * out from under the MW board the moment an operator switched tabs.
 *
 * It answers two things, because the two boards need different halves of it.
 *
 * `model` is the facade, and it exists whenever a view does — the orbital board
 * needs **no geometry at all** (rings, slots and debris are on the wire), so a
 * scenario with no map pack still has a working SDA board and `REGIONS` is
 * simply empty. Wiring the SDA board to the theatre's pack would have made it
 * answer `NO MAP PACK FOR THIS SCENARIO`, which is true and is not the reason.
 *
 * `theatre` is the five states the region map has, and it draws four of them,
 * because *why* there is no map decides what the operator should do about it:
 *
 * | | |
 * | --- | --- |
 * | `loading` | the pack has not resolved yet |
 * | `absent` | no pack for this scenario — geometry is optional (M5-D11) |
 * | `invalid` | a pack that is not a pack: somebody has to fix the file |
 * | `incomplete` | a pack that does not cover a region the wire declares |
 * | `ready` | the facade, and the `[lon, lat]` per region the MW map needs |
 *
 * `incomplete` is the one the plan asks for by name — *"a renamed region fails
 * loudly rather than dropping a marker in the sea"* — and it is loud because
 * `region-map.js` is not: `buildRings` `console.warn`s a source it cannot
 * resolve and carries on with a theatre quietly missing a quarter of itself.
 */

export type ScenarioFacadeState =
  | { readonly status: 'loading' }
  | { readonly status: 'absent' }
  | { readonly status: 'invalid'; readonly reason: string }
  | { readonly status: 'incomplete'; readonly missing: readonly string[] }
  | {
      readonly status: 'ready';
      readonly model: ScenarioModel;
      /** Region id → `[lon, lat]`. The argument `mwMapRecords` has been waiting for. */
      readonly geometry: MwGeometry;
    };

export interface ScenarioFacade {
  /** The region map's answer: four notices and a ready state. */
  readonly theatre: ScenarioFacadeState;
  /**
   * The facade every map reads, whenever there is a view to build one from.
   *
   * The same object `theatre.model` carries when the pack resolved — built once,
   * adopted once. Without a pack it is the same model with no `REGIONS`, which
   * is all the orbital board ever needed.
   */
  readonly model: ScenarioModel | null;
}

export function useScenarioFacade(view: CellViewWire | null, scenario: string): ScenarioFacade {
  const pack = useMapPack(scenario);

  const facade = useMemo<ScenarioFacade>(() => {
    if (view === null || pack.status === 'loading') {
      return { theatre: { status: 'loading' }, model: null };
    }
    if (pack.status !== 'ready') {
      // No pack, or an unreadable one. The theatre says which; the orbital board
      // carries on with a model that has no geography in it.
      const built = scenarioModel(view, null);
      return { theatre: pack, model: built.ok ? built.model : null };
    }
    const built = scenarioModel(view, pack.pack);
    if (!built.ok) {
      // A region the pack cannot place is a *theatre* failure, and the model it
      // would have produced is the one the orbital board is entitled to.
      const orbital = scenarioModel(view, null);
      return {
        theatre: { status: 'incomplete', missing: built.missing },
        model: orbital.ok ? orbital.model : null,
      };
    }
    return {
      theatre: { status: 'ready', model: built.model, geometry: packGeometry(pack.pack) },
      model: built.model,
    };
  }, [view, pack]);

  const model = facade.model;
  useEffect(() => {
    if (model !== null) adopt(model);
  }, [model]);

  // Released when the console goes away, and not when a tab does.
  useEffect(() => release, []);

  return facade;
}
